import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { appApi } from '../api/app';
import { perusahaanApi } from '../api/perusahaan';
import { ApiException } from '../api/client';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { AccentCard } from '../components/Card';
import { Input } from '../components/Input';
import { AppLayout } from '../components/Layout';
import { Select } from '../components/Select';
import type { PerusahaanSummary } from '../types/api';

export function LoginAplikasiPage() {
  const navigate = useNavigate();
  const [perusahaanList, setPerusahaanList] = useState<PerusahaanSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [idPerusahaan, setIdPerusahaan] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const list = await perusahaanApi.mine();
        setPerusahaanList(list);
        // Kalau hanya 1, preselect
        if (list.length === 1) setIdPerusahaan(list[0].idPerusahaan);
      } catch (err) {
        setListError(err instanceof ApiException ? err.message : 'Gagal memuat daftar perusahaan.');
      } finally {
        setLoadingList(false);
      }
    };
    run();
  }, []);

  const perusahaanAktif = perusahaanList.filter((p) => p.status === 'Aktif' && p.tanggalProvisioning);

  const options = perusahaanAktif.map((p) => ({
    value: p.idPerusahaan,
    label: `${p.namaPerusahaan} (${p.idPerusahaan})`,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Buka tab kosong DULU di dalam user gesture (synchronous) — kalau dibuka
    // setelah await, popup blocker browser akan menolaknya. Tab kosong ini
    // di-fill setelah dapat redirectUrl dari backend.
    //
    // CATATAN: tidak pakai 'noopener'/'noreferrer' karena flag itu membuat
    // window.open return null → kita kehilangan reference untuk set location.
    // Trade-off security minimal: destination app.booku.id adalah aplikasi
    // kita sendiri (trusted), bukan link eksternal arbitrary.
    const newTab = window.open('about:blank', '_blank');

    try {
      const result = await appApi.login({
        idPerusahaan: idPerusahaan.trim().toLowerCase(),
        username: username.trim().toLowerCase(),
        password,
      });

      if (newTab && !newTab.closed) {
        newTab.location.href = result.redirectUrl;
        setSuccess('Login berhasil. Aplikasi Booku terbuka di tab baru.');
      } else {
        // Popup blocker memblokir, atau user keburu menutup tab.
        // Fallback: redirect tab saat ini.
        setSuccess('Login berhasil. Mengarahkan ke aplikasi Booku...');
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 800);
      }
    } catch (err) {
      // Tutup tab kosong yang sudah terlanjur dibuka kalau login gagal.
      if (newTab && !newTab.closed) newTab.close();
      setError(err instanceof ApiException ? err.message : 'Terjadi kesalahan tak terduga.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingList) {
    return (
      <AppLayout>
        <AccentCard title="Login ke Aplikasi Booku">
          <div className="text-sm text-gray-500">Memuat daftar perusahaan...</div>
        </AccentCard>
      </AppLayout>
    );
  }

  if (listError) {
    return (
      <AppLayout>
        <AccentCard title="Login ke Aplikasi Booku">
          <Alert variant="error">{listError}</Alert>
        </AccentCard>
      </AppLayout>
    );
  }

  if (perusahaanList.length === 0) {
    return (
      <AppLayout>
        <AccentCard title="Login ke Aplikasi Booku">
          <Alert variant="info" title="Belum ada perusahaan">
            Anda belum mendaftarkan perusahaan. Silakan daftarkan perusahaan dulu untuk bisa login ke aplikasi.
          </Alert>
          <div className="mt-5">
            <Button variant="primary" fullWidth onClick={() => navigate('/perusahaan/daftar')}>
              Daftarkan Perusahaan
            </Button>
          </div>
        </AccentCard>
      </AppLayout>
    );
  }

  if (perusahaanAktif.length === 0) {
    return (
      <AppLayout>
        <AccentCard title="Login ke Aplikasi Booku">
          <Alert variant="warning" title="Belum ada perusahaan aktif">
            Perusahaan yang Anda daftarkan belum diaktifkan oleh admin Booku. Mohon tunggu approval.
          </Alert>
          <div className="mt-5">
            <Link to="/dashboard">
              <Button variant="outline" fullWidth>
                Kembali ke Dashboard
              </Button>
            </Link>
          </div>
        </AccentCard>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <AccentCard
        title="Login ke Aplikasi Booku"
        subtitle="Pilih perusahaan lalu masuk dengan Super User aplikasi"
      >
        {success && (
          <div className="mb-4">
            <Alert variant="success">{success}</Alert>
          </div>
        )}

        {error && (
          <div className="mb-4">
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <Select
            label="Perusahaan"
            value={idPerusahaan}
            onChange={(e) => setIdPerusahaan(e.target.value)}
            placeholder="Pilih perusahaan..."
            options={options}
            required
            disabled={loading || !!success}
            hint={
              perusahaanAktif.length === 1
                ? 'Hanya 1 perusahaan — sudah dipilih otomatis.'
                : `${perusahaanAktif.length} perusahaan tersedia.`
            }
          />
          <Input
            label="Username Aplikasi"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username Super User yang Anda daftarkan"
            required
            autoComplete="off"
            disabled={loading || !!success}
          />
          <Input
            label="Password Aplikasi"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="off"
            disabled={loading || !!success}
            hint="Berbeda dengan password klien."
          />
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => navigate('/dashboard')} disabled={loading}>
              Batal
            </Button>
            <Button type="submit" variant="primary" fullWidth loading={loading} disabled={!!success}>
              Masuk ke Booku
            </Button>
          </div>
        </form>
      </AccentCard>
    </AppLayout>
  );
}
